import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import SectionHeader from "../../../../util/component/SectionHeader";
import {useAffiliateActions} from "../../service/useAffiliateActions";
import {Button, Grid, MenuItem, Paper, TextField} from "@mui/material";
import AffiliateAccordionDetail from "./modals/AffiliateAccordionDetail";
import {Controller, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import AffiliateUpdateModal from "./modals/AffiliateUpdateModal";
import {AFFILIATE_STATUS_DISMISSED, AFFILIATE_STATUS_READY} from "../../component/Constants";
import {useStatusValuesAffiliate} from "../../component/useStatusValuesAffiliate";
import {
    UserLegalEntitiesOrDealersInputGroup
} from "../../component/UserLegalEntitiesOrDealersInputGroup/UserLegalEntitiesOrDealersInputGroup";

const AffiliateUpdatePage = () => {
    const { t } = useTranslation(['salesmarketing', 'translation']);
    const { getStatusLabelAffiliate } = useStatusValuesAffiliate();
    const navigate = useNavigate();
    const { affiliateId } = useParams();
    const [modalOpen, setModalOpen] = useState(false);
    const [detail, setDetail] = useState({
        tenantId: "",
        dealerId: "",
        genVendorCode: "",
        networkBusinessTravelCode: "",
        typology: "",
        status: "",
        naming: "",
        bpCode: "",
        bpKind: "",
        iataCode: "",
        serviceType: [],
        country: "",
        region: "",
        city: "",
        address: "",
        streetNumber: "",
        postalCode: "",
        province: "",
        phoneNumber: "",
        cellPhoneNumber: "",
        email: "",
        area: "",
        website: "",
        incomeCode: "",
        percipient: "",
        percipientAddress: "",
        percipientCity: "",
        percipientProvince: "",
        percipientPostalCode: "",
        repCompanyName: "",
        repVatNumber: "",
        siren: "",
        siret: "",
        representativeName: "",
        representativeEmail: "",
        representativeCellphone: "",
        taxCode: "",
        sdiCode: "",
        representativeContactName: "",
        representativeContactEmail: "",
        representativeContactCellphone: "",
        representativeIban: "",
        representativeAddress: "",
        representativeStreetNumber: "",
        representativePostalCode: "",
        representativeCity: "",
        representativeProvince: "",
        representativeHqCellphone: "",
        representativePec: ""
    });
    const affiliateActions = useAffiliateActions();

    const { handleSubmit, control, reset } = useForm({
        defaultValues: {
            status: detail?.status,
            networkBusinessTravelCode: detail?.networkBusinessTravelCode,
            typology: detail?.typology
        },
        resolver: yupResolver(Yup.object({
            status: Yup.string().nullable(),
            networkBusinessTravelCode: Yup.string().nullable(),
            typology: Yup.string().nullable()
        }))
    });

    const fetchDetail = async (affiliateId: any) => {
        let res = await affiliateActions.detailAffiliate(affiliateId);
        setDetail(res);
        reset(res);
    }

    useEffect(() => {
        fetchDetail(affiliateId);
    }, [])

    const onSubmit = async (data: any) => {
        data.genBpCode = affiliateId;
        await affiliateActions.updateAffiliate(data);
        setModalOpen(true);
    }

    const statuses = [
        {
            key:AFFILIATE_STATUS_READY,
            value: getStatusLabelAffiliate(AFFILIATE_STATUS_READY)
        },
        {
            key: AFFILIATE_STATUS_DISMISSED,
            value: getStatusLabelAffiliate(AFFILIATE_STATUS_DISMISSED)
        }
    ]

    return (
        <>
            <SectionHeader title={t('salesmarketing:affiliate.updateHeader.title')} subtitle={t('salesmarketing:affiliate.updateHeader.subTitle')} />
            <Paper elevation={0} sx={{ padding: "30px" }}>
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <Grid container spacing={2}>
                        <UserLegalEntitiesOrDealersInputGroup
                            tenantId={detail.tenantId}
                            dealerId={detail.dealerId}
                            disabled={!!affiliateId}
                        />
                        <Grid container item xs={12} spacing={1}>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    label={t('salesmarketing:affiliate.detailContent.companyNameField')}
                                    disabled
                                    value={detail?.repCompanyName}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    label={t('salesmarketing:affiliate.detailContent.vatNumberField')}
                                    disabled
                                    value={detail?.repVatNumber}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    label={t('salesmarketing:affiliate.detailContent.sirenField')}
                                    disabled
                                    value={detail?.siren}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    label={t('salesmarketing:affiliate.detailContent.siretField')}
                                    disabled
                                    value={detail?.siret}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    label={t('salesmarketing:affiliate.detailContent.vendorCodeField')}
                                    disabled
                                    value={detail?.genVendorCode}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    label={t('salesmarketing:affiliate.detailContent.bpCodeField')}
                                    disabled
                                    value={detail?.bpCode}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Controller
                                    control={control}
                                    name={"status"}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            fullWidth
                                            label={t('salesmarketing:affiliate.detailContent.statusField')}
                                            select
                                            value={field.value}
                                            onChange={field.onChange}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        >
                                            {statuses?.map((s) => (
                                                <MenuItem key={s.key} value={s.key}>
                                                    {s.value}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )} />
                            </Grid>
                            <Grid item xs={3}>
                                <Controller
                                    control={control}
                                    name={"networkBusinessTravelCode"}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            fullWidth
                                            label={t('salesmarketing:affiliate.detailContent.networkBusinessTravelCodeField')}
                                            value={field.value}
                                            onChange={field.onChange}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        />
                                    )} />
                            </Grid>
                            <Grid item xs={3}>
                                <Controller
                                    control={control}
                                    name={"typology"}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            fullWidth
                                            label={t('salesmarketing:affiliate.detailContent.typologyField')}
                                            value={field.value}
                                            onChange={field.onChange}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        />
                                    )} />
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <AffiliateAccordionDetail detail={detail} isDisabled={true} />
                        </Grid>
                        <Grid container rowSpacing={3} columnSpacing={3} sx={{paddingTop: "50px"}}>
                            <Grid container item xs={12} rowSpacing={1} columnSpacing={3}
                                  justifyContent={"flex-end"}>
                                <Grid item>
                                    <Button fullWidth variant="outlined" component="label"  onClick={() => { navigate("/salesmarketing/affiliatelist") }}>
                                        {t('salesmarketing:affiliate.buttons.cancel')}
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button fullWidth color="accent" size="small" type="submit">{t('salesmarketing:affiliate.buttons.confirm')}</Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
            <AffiliateUpdateModal open={modalOpen} setOpen={setModalOpen} />
        </>
    );
}

export default AffiliateUpdatePage;